// Shared mock state and helpers for the portal React app

import React from 'react';

interface PortalUser {
  id: number;
  name: string;
  email: string;
  login: string;
}

function getPortalUser(): PortalUser | null {
  const storedUser = localStorage.getItem("user");

  if (!storedUser) return null;

  try {
    return JSON.parse(storedUser) as PortalUser;
  } catch {
    return null;
  }
}

// Type definitions
export type Route = "dashboard" | "products" | "downloads" | "repos" | "docs" | "support" | "account" | "admin";
export type Role = "Org Admin" | "Standard User";
export type MaintenanceStatus = "Active" | "ExpiringSoon" | "Expired";
export type TicketStatus = "Open" | "Waiting on Customer" | "Closed" | "Escalated";
export type Severity = "Low" | "Medium" | "High" | "Critical";

export interface Maintenance {
  through: string;
  status: MaintenanceStatus;
}

export interface Product {
  id: number;
  name: string;
  model_num?: string;
  support_email?: string;
}

export interface Release {
  id: number;
  product: string;
  version: string;
  date: string;
  released: boolean;
  notes: string;
}

export interface Download {
  product: string;
  version: string;
  platform: string;
  type: string;
  size: string;
  access: string;
  file: string;
}

export interface Doc {
  id: string;
  product: string;
  version: string;
  category: string;
  title: string;
  tags: string[];
  body: string;
}

export interface TicketMessage {
  by: string;
  when: string;
  text: string;
}

export interface Ticket {
  id: string;
  product: string;
  version: string;
  status: TicketStatus;
  severity: Severity;
  created: string;
  updated: string;
  subject: string;
  owner: string;
  messages: TicketMessage[];
}

export interface Organization {
  name: string;
  maintenance: Record<string, Maintenance>;
}

export interface DownloadFilters {
  product: string;
  version: string;
  platform: string;
  type: string;
}

export interface DocFilters {
  product: string;
  version: string;
  category: string;
  query: string;
}

export interface SupportFilters {
  status: string;
  product: string;
}

export interface AppState {
  route: Route;
  role: Role;
  org: Organization;
  products: Product[];
  releases: Release[];
  downloads: Download[];
  docs: Doc[];
  tickets: Ticket[];
  selectedTicketId: string | null;
  globalQuery: string;
  downloadFilters: DownloadFilters;
  docFilters: DocFilters;
  supportFilters: SupportFilters;
}

export interface RouteMeta {
  title: string;
  subtitle: string;
  primaryLabel: string;
  primaryOnClick?: () => void;
}

export const initialState: AppState = {
  route: "dashboard",
  role: "Org Admin", // or "Standard User"
  org: {
    name: "CCRT",
    maintenance: {
      "RedHawk Linux": { through: "2026-12-31", status: "Active" },
      "SIMulation Workbench": { through: "2026-03-15", status: "ExpiringSoon" }
    }
  },
  products: [
    { id: 1, name: "RedHawk Linux", model_num: "RH-LNX", support_email: "support@concurrent-rt.com" },
    { id: 2, name: "SIMulation Workbench", model_num: "SIM-WB", support_email: "support@concurrent-rt.com" },
  ],
  releases: [
    { id: 101, product: "RedHawk Linux", version: "8.4.1", date: "2026-01-20", released: true, notes: "Minor stability improvements and installer updates." },
    { id: 102, product: "RedHawk Linux", version: "8.3.3", date: "2025-10-02", released: true, notes: "Security patches and driver updates." },
    { id: 201, product: "SIMulation Workbench", version: "2025.3", date: "2026-01-05", released: true, notes: "Improved model import and analysis workflow." },
    { id: 202, product: "SIMulation Workbench", version: "2025.2", date: "2025-11-18", released: true, notes: "Bug fixes and performance improvements." },
  ],
  downloads: [
    { product:"RedHawk Linux", version:"8.4.1", platform:"x86_64", type:"ISO", size:"2.4 GB", access:"Entitled", file:"redhawk-8.4.1-x86_64.iso" },
    { product:"RedHawk Linux", version:"8.4.1", platform:"x86_64", type:"Repo", size:"—", access:"Entitled", file:"repo/redhawk/8.4.1/x86_64" },
    { product:"RedHawk Linux", version:"8.3.3", platform:"x86_64", type:"ISO", size:"2.2 GB", access:"Entitled", file:"redhawk-8.3.3-x86_64.iso" },
    { product:"SIMulation Workbench", version:"2025.3", platform:"Windows", type:"Installer", size:"1.1 GB", access:"Entitled", file:"simwb-2025.3-win64.exe" },
    { product:"SIMulation Workbench", version:"2025.2", platform:"Windows", type:"Installer", size:"1.0 GB", access:"Entitled", file:"simwb-2025.2-win64.exe" },
  ],
  docs: [
    { id:"KB-101", product:"RedHawk Linux", version:"8.4.1", category:"Installation", title:"Installing RedHawk Linux 8.4.1 on iHawk", tags:["install","ihawk","iso"], body:"Step-by-step install guidance...\n\n1) Verify ISO checksum...\n2) Boot installer...\n3) Configure RT settings...\n\nTroubleshooting:\n- If NIC not detected..." },
    { id:"KB-114", product:"RedHawk Linux", version:"8.4.1", category:"Performance", title:"Tuning for determinism: baseline RT settings", tags:["latency","determinism","tuning"], body:"Recommended baseline settings...\n\n- CPU isolation\n- IRQ affinity\n- Timer settings\n\nValidation steps..." },
    { id:"KB-205", product:"SIMulation Workbench", version:"2025.3", category:"Getting Started", title:"First model run in SIMulation Workbench", tags:["getting-started","models"], body:"Create project → import model → run simulation...\n\nTips:\n- Use recommended sample rates\n- Validate IO mapping..." },
    { id:"KB-222", product:"SIMulation Workbench", version:"2025.2", category:"Troubleshooting", title:"Common import errors and fixes", tags:["import","errors"], body:"If you see error X...\n\nFix:\n- Ensure schema compatibility\n- Validate dependencies..." },
  ],
  tickets: [
    {
      id:"CCRT-2481", product:"RedHawk Linux", version:"8.4.1", status:"Waiting on Customer", severity:"High",
      created:"2026-02-03", updated:"2026-02-10", subject:"Determinism regression after driver update",
      owner:"Support", messages:[
        { by:"Steve", when:"2026-02-03 09:12", text:"Seeing jitter spikes after updating driver package. Steps to reproduce included." },
        { by:"Support", when:"2026-02-04 14:18", text:"Thanks. Can you share your IRQ affinity + CPU isolation settings?" },
        { by:"Steve", when:"2026-02-10 11:06", text:"Attached settings. CPU isolated: 2-15; IRQ pinned: 2-7." },
      ]
    },
    {
      id:"CCRT-2510", product:"SIMulation Workbench", version:"2025.3", status:"Open", severity:"Medium",
      created:"2026-02-09", updated:"2026-02-11", subject:"Model import fails on specific block type",
      owner:"Support", messages:[
        { by:"Steve", when:"2026-02-09 16:22", text:"Import fails with error 'unsupported block type'. Can we get guidance?" },
        { by:"Support", when:"2026-02-11 10:41", text:"Please confirm export format + attach minimal reproducer model." },
      ]
    }
  ],
  selectedTicketId: null,
  globalQuery: "",
  downloadFilters: { product:"All", version:"All", platform:"All", type:"All" },
  docFilters: { product:"All", version:"All", category:"All", query:"" },
  supportFilters: { status:"All", product:"All" }
};

export const fmtDate = (d: string): string => d;

export const uniq = <T,>(arr: T[]): T[] => Array.from(new Set(arr));

export function matchesGlobal<T extends object>(
  item: T,
  query: string
): boolean {
  const q = query.trim().toLowerCase();

  if (!q) return true;

  return Object.values(item).some((value) => {
    if (value === null || value === undefined) return false;

    if (typeof value === "string" || typeof value === "number") {
      return String(value).toLowerCase().includes(q);
    }

    if (Array.isArray(value)) {
      return value.some((v) => String(v).toLowerCase().includes(q));
    }

    return false;
  });
}

export function badgeForMaint(product: string, org: Organization): React.ReactElement {
  const m = org.maintenance[product];
  if (!m) return <span className="badge">● Unknown</span>;
  if (m.status === "Active") return <span className="badge ok">● Active · thru {m.through}</span>;
  if (m.status === "ExpiringSoon") return <span className="badge warn">● Expiring · thru {m.through}</span>;
  if (m.status === "Expired") return <span className="badge danger">● Expired · thru {m.through}</span>;
  return <span className="badge">● {m.status} · thru {m.through}</span>;
}

export function BadgeForStatus({ status }: { status: string }): React.ReactElement {
  const s = String(status || "").toLowerCase();
  let cls = "";
  if (s.includes("open")) cls = "ok";
  if (s.includes("waiting")) cls = "warn";
  if (s.includes("closed")) cls = "";
  if (s.includes("escal")) cls = "danger";
  return <span className={`badge ${cls}`.trim()}>● {status}</span>;
}

export function severityClass(sev: string): string {
  const s = String(sev).toLowerCase();
  if (s.includes("critical")) return "danger";
  if (s.includes("high")) return "warn";
  if (s.includes("medium")) return "ok";
  return "";
}

export function todayStr(): string {
  return "2026-02-12";
}

export function nowStr(): string {
  return "2026-02-12 09:00";
}

export function routeMeta(route: Route, role: Role, onPrimaryClick: () => void): RouteMeta {
  switch(route){
    case "dashboard":
      return {
        title:"Dashboard",
        subtitle:"Overview of entitlements, downloads, docs, and support.",
        primaryLabel:"New Ticket",
        primaryOnClick: onPrimaryClick,
      };
    case "products":
      return {
        title:"Products",
        subtitle:"Your entitled products, coverage, and quick actions.",
        primaryLabel:"Request Access",
        primaryOnClick: () => window.alert("Mock: Request access flow (future)."),
      };
    case "downloads":
      return {
        title:"Downloads",
        subtitle:"Filter releases by product, version, platform, and type.",
        primaryLabel:"Copy Repo URL",
        primaryOnClick: () => window.alert("Mock: Copied repo URL."),
      };
      case "repos":
      return {
        title:"Repos",
        subtitle:"Browse entitled software repositories.",
        primaryLabel:"Refresh",
      };
    case "docs":
      return {
        title:"Documentation",
        subtitle:"Knowledge base with search and version/product filters.",
        primaryLabel:"Suggest Edit",
        primaryOnClick: () => window.alert("Mock: Suggest edit / feedback flow."),
      };
    case "support":
      return {
        title:"Support",
        subtitle:"Submit and track tickets. Org Admins can view all org tickets.",
        primaryLabel:"New Ticket",
        primaryOnClick: onPrimaryClick,
      };
    case "account":
      return {
        title:"Account",
        subtitle:"Profile, organization, security, and user management.",
        primaryLabel:"Invite User",
        primaryOnClick: () => {
          if (role === "Org Admin") {
            window.alert("Mock: Invite user modal.");
          } else {
            window.alert("Admins only.");
          }
        },
      };
      
    default:
      return { title:"Portal", subtitle:"", primaryLabel:"Action", primaryOnClick: () => {} };
  }
}
